import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import {
  type ActivatedRouteSnapshot,
  NavigationEnd,
  Router,
  RouterModule,
} from '@angular/router';
import { Store } from '@ngrx/store';
import { type Observable, map, startWith } from 'rxjs';
import { filter } from 'rxjs/operators';
import type { PlatformAdminData } from '../../+state/platform-identity/platform-identity.models';
import { selectCurrentAdmin } from '../../+state/platform-identity/platform-identity.selectors';
import type { RootState } from '../../common/interfaces/root-state.interface';
import { SidebarNavigationComponent } from './components/sidebar-navigation/sidebar-navigation.component';
import { PlatformAdminFooterComponent } from './footer/footer.component';

@Component({
  selector: 'cdw-internal-layout',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    SidebarNavigationComponent,
    PlatformAdminFooterComponent,
    MatProgressSpinnerModule,
  ],
  templateUrl: './internal-layout.component.html',
  styleUrls: ['./internal-layout.component.scss'],
})
export class InternalLayoutComponent {
  private store = inject<Store<RootState>>(Store);
  private router = inject(Router);

  admin$: Observable<PlatformAdminData | null> =
    this.store.select(selectCurrentAdmin);

  userInfo$: Observable<{
    name: string;
    avatarUrl: string;
    email: string | null;
  } | null> = this.admin$.pipe(
    map((admin) =>
      admin
        ? {
            name: admin.name || admin.email || 'Admin',
            avatarUrl: admin.photoURL || 'images/gopher.svg',
            email: admin.email,
          }
        : null
    )
  );

  // Header title derived from the deepest active route's `headerTitle` data.
  headerText$: Observable<string> = this.router.events.pipe(
    filter((event) => event instanceof NavigationEnd),
    startWith(null),
    map(() => this.resolveHeaderTitle())
  );

  private resolveHeaderTitle(): string {
    let route: ActivatedRouteSnapshot | null =
      this.router.routerState.snapshot.root;
    let title = '';
    while (route) {
      const data = route.data as { headerTitle?: string };
      if (data?.headerTitle) {
        title = data.headerTitle;
      }
      route = route.firstChild;
    }
    return title;
  }
}
